import type { DeviationStatus, Protocol, ProtocolView, RoundStatus } from '../../../api/safetyRounds';
import { Routes } from '../../../enums/Routes';
import { managerPlanPath, planPath } from '../Plans/plansView';

export const protocolPath = (roundId: number) => Routes.SafetyRepRoundProtocol.replace(':roundId', String(roundId));

/** The general manager reads and signs the protocol in their own part of the app */
export const managerProtocolPath = (roundId: number) => Routes.GeneralManagerProtocol.replace(':roundId', String(roundId));

/** The protocol where the reader works with safety rounds: as safety representative, or else as general manager */
export const protocolPathFor = (roundId: number, roles: string[]) =>
  !roles.includes('SAFETY_REP') && roles.includes('GENERAL_MANAGER') ? managerProtocolPath(roundId) : protocolPath(roundId);

/** «Mine varsler» for the reader's role. The general manager has their own, with only their emails. */
export const notificationsPathFor = (roles: string[]) =>
  !roles.includes('SAFETY_REP') && roles.includes('GENERAL_MANAGER') ? Routes.GeneralManagerNotifications : Routes.SafetyRoundNotifications;

export const STATUS_COLORS: Record<RoundStatus, 'info' | 'warning' | 'success' | 'neutral'> = {
  PLANNED: 'neutral',
  IN_PROGRESS: 'info',
  AWAITING_SIGNATURE: 'warning',
  SIGNED: 'success',
};

export const DEVIATION_COLORS: Record<DeviationStatus, 'danger' | 'warning' | 'success' | 'info'> = {
  OPEN: 'danger',
  FIXED: 'warning',
  AWAITING_DECISION: 'info',
  CLOSED: 'success',
  ACCEPTED: 'success',
};

const slug = (text: string) =>
  text
    .toLowerCase()
    .normalize('NFKD')
    .replace(/æ/g, 'ae')
    .replace(/ø/g, 'o')
    .replace(/å/g, 'a')
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-|-$/g, '');

/** «vernerunde-vernerunde-kontor-2026-04-10.pdf», as the handoff names it */
export const protocolFileName = (protocol: Pick<Protocol, 'planName' | 'completedOn' | 'plannedDate'>) =>
  `vernerunde-${slug(protocol.planName)}-${protocol.completedOn ?? protocol.plannedDate}.pdf`;

export type SignatureState = 'signed' | 'waiting' | 'afterSafetyRep';

/** The general manager signs after the safety representative, so their box says so until then */
export const signatureState = (protocol: Pick<Protocol, 'signatures'>, role: 'SAFETY_REP' | 'GENERAL_MANAGER'): SignatureState => {
  if (protocol.signatures.some((signature) => signature.role === role)) return 'signed';
  if (role === 'GENERAL_MANAGER' && !protocol.signatures.some((signature) => signature.role === 'SAFETY_REP'))
    return 'afterSafetyRep';
  return 'waiting';
};

export const signatureOf = (protocol: Pick<Protocol, 'signatures'>, role: 'SAFETY_REP' | 'GENERAL_MANAGER') =>
  protocol.signatures.find((signature) => signature.role === role) ?? null;

/** The newest request for a change, which the safety representative must see */
export const latestChangeRequest = (protocol: Pick<Protocol, 'changeRequests'>) =>
  protocol.changeRequests[protocol.changeRequests.length - 1] ?? null;

/**
 * «Vernerunder › plan › Protokoll», where the reader works with safety rounds: the general manager's tasks when they
 * came from there or have no plans of their own, the safety representative's plans, or HR's overview.
 */
export const protocolCrumbs = (roles: string[], path: string, planId: number) =>
  path.startsWith('/daglig-leder/') || (!roles.includes('SAFETY_REP') && roles.includes('GENERAL_MANAGER'))
    ? { home: Routes.GeneralManagerTasks, homeLabel: 'tasks' as const, plan: managerPlanPath(planId) }
    : roles.includes('SAFETY_REP')
      ? { home: Routes.SafetyRepRoundPlans, homeLabel: 'rounds' as const, plan: planPath(planId) }
      : { home: Routes.HRSafetyRoundOverviewFullPath, homeLabel: 'rounds' as const, plan: null };

export type ProtocolState = 'yourSignature' | 'sentBack' | 'waitsForSafetyRep' | 'waitsForManager' | 'SIGNED';

/** The tag at the top: whose turn it is, said to the reader */
export const protocolState = (
  view: Pick<ProtocolView, 'status' | 'maySignAsGeneralManager' | 'readerIsSafetyRep' | 'protocol'>,
): ProtocolState => {
  if (view.status === 'SIGNED') return 'SIGNED';
  if (view.status === 'AWAITING_SIGNATURE') return view.maySignAsGeneralManager ? 'yourSignature' : 'waitsForManager';
  if (!view.readerIsSafetyRep) return 'waitsForSafetyRep';
  return view.status === 'IN_PROGRESS' && latestChangeRequest(view.protocol) ? 'sentBack' : 'yourSignature';
};

export const STATE_COLORS: Record<ProtocolState, 'accent' | 'warning' | 'success' | 'neutral'> = {
  yourSignature: 'accent',
  sentBack: 'warning',
  waitsForSafetyRep: 'neutral',
  waitsForManager: 'warning',
  SIGNED: 'success',
};

export type StepState = 'done' | 'current' | 'next';

/** The three steps of signing: the safety representative, the general manager, and the deviations going out */
export const signingSteps = (
  view: Pick<ProtocolView, 'status' | 'protocol'>,
): Array<{ key: 'safetyRep' | 'generalManager' | 'deviations'; state: StepState; date: string | null }> => {
  const current = view.status === 'SIGNED' ? 4 : view.status === 'AWAITING_SIGNATURE' ? 2 : 1;
  const state = (step: number): StepState => (step < current ? 'done' : step === current ? 'current' : 'next');
  const managerSigned = signatureOf(view.protocol, 'GENERAL_MANAGER')?.signedAt ?? null;
  return [
    { key: 'safetyRep', state: state(1), date: signatureOf(view.protocol, 'SAFETY_REP')?.signedAt ?? null },
    { key: 'generalManager', state: state(2), date: managerSigned },
    // The deviations go out with the general manager's signature
    { key: 'deviations', state: state(3), date: managerSigned },
  ];
};

export type ResultPart = 'ok' | 'deviations' | 'notChecked' | 'notRelevant' | 'unanswered';

/** The parts of the result bar and its legend, in order, without the empty ones */
export const resultParts = (counts: Protocol['counts']): Array<{ kind: ResultPart; count: number }> => {
  const answered = counts.ok + counts.deviations + counts.notChecked + counts.notRelevant;
  return (
    [
      { kind: 'ok', count: counts.ok },
      { kind: 'deviations', count: counts.deviations },
      { kind: 'notChecked', count: counts.notChecked },
      { kind: 'notRelevant', count: counts.notRelevant },
      { kind: 'unanswered', count: Math.max(counts.total - answered, 0) },
    ] as Array<{ kind: ResultPart; count: number }>
  ).filter((part) => part.count > 0);
};
